import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MessageSquareWarning } from "lucide-react";
import { logger } from "@/lib/logger";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { TruncatedText } from "@/components/ui/truncated-text";

/**
 * TES-2229 US3/S5 (LH §L5.10): „Storno anregen (auditiert)" — der Fachbereich
 * (mangelbearbeiter/Gruppen-Owner/-Deputy, tester außerhalb Fenster) darf NICHT
 * selbst stornieren, sondern schlägt es dem zuständigen QM vor. Server-Flow:
 * rpc_suggest_defect_cancellation = RLS-geprüfter Kommentar ([Storno angeregt] …)
 * + defect_cancel_requested-Notification an verifying_qm ?? Team-QMs + Audit-Spur.
 *
 * data-testid-Konvention (TES-2217-Anschluss, PoolRow-Affordance): Trigger heißen
 * app-weit `suggest-cancel-action`, der Dialog-Confirm `suggest-cancel-confirm`.
 */
type SuggestCancelRpc = {
  rpc: (
    fn: "rpc_suggest_defect_cancellation",
    args: { _defect_id: string; _reason: string },
  ) => Promise<{ error: { code?: string; message: string } | null }>;
};

interface SuggestCancelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defect: { id: string; title: string } | null;
  onSuggested?: () => void;
}

export function SuggestCancelDialog({
  open,
  onOpenChange,
  defect,
  onSuggested,
}: SuggestCancelDialogProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const trimmedReason = reason.trim();

  const handleSuggest = async () => {
    if (!defect || !trimmedReason) return;

    setIsSubmitting(true);
    try {
      const { error } = await (supabase as unknown as SuggestCancelRpc).rpc(
        "rpc_suggest_defect_cancellation",
        { _defect_id: defect.id, _reason: trimmedReason },
      );

      if (error) {
        logger.error("Error suggesting defect cancellation:", error);
        toast.error(t("common.error"), {
          description: t("defects.suggestCancel.errorDescription"),
        });
        return;
      }

      toast.success(t("defects.suggestCancel.successTitle"), {
        description: t("defects.suggestCancel.successDescription"),
      });

      setReason("");
      onOpenChange(false);
      onSuggested?.();
    } catch (error) {
      logger.error("Error suggesting defect cancellation:", error);
      toast.error(t("common.error"), {
        description: t("defects.suggestCancel.errorDescription"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) setReason("");
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md w-[calc(100%-2rem)]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquareWarning className="h-5 w-5 text-muted-foreground" />
            {t("defects.suggestCancel.title")}
          </DialogTitle>
          <DialogDescription>{t("defects.suggestCancel.description")}</DialogDescription>
        </DialogHeader>

        {defect && (
          <div className="space-y-4">
            <div className="p-3 bg-muted rounded-md min-w-0">
              <TruncatedText text={defect.title} className="font-medium" maxLines={2} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="suggest-cancel-reason">{t("defects.suggestCancel.reason")}</Label>
              <Textarea
                id="suggest-cancel-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t("defects.suggestCancel.reasonPlaceholder")}
                rows={3}
                data-testid="suggest-cancel-reason"
              />
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isSubmitting}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleSuggest}
            disabled={isSubmitting || !trimmedReason}
            data-testid="suggest-cancel-confirm"
          >
            {isSubmitting ? t("common.saving") : t("defects.suggestCancel.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
